<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="520px" scrollable :persistent="sending" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card tile elevation="0" class="zoezi-withdrawal-dialog">
            <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                <span>{{ $translate('Withdraw from purchase') }}</span>
                <v-spacer />
                <v-btn icon :disabled="sending" :aria-label="$translate('Close')" @click="close"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-text v-if="step == 'expired' || step == 'invalid'" class="text-center pt-8">
                <v-avatar size="64" :color="stateIcon.background">
                    <v-icon large :color="stateIcon.color">{{ stateIcon.icon }}</v-icon>
                </v-avatar>
                <p class="mt-6 text--primary font-weight-medium">{{ stateText }}</p>
            </v-card-text>

            <v-card-text v-else-if="step == 'lines'" class="pt-4">
                <div class="text-caption text--primary">{{ purchaseHeading }}</div>
                <div v-if="hasWithdrawableLine" class="text--primary font-weight-medium">{{ $translate('Choose what you want to withdraw from. You withdraw one thing at a time.') }}</div>
                <v-list outlined class="py-0 mt-3">
                    <template v-for="(line, index) in linesToShow">
                        <v-divider v-if="index > 0" :key="'divider-' + line.payment_id" />
                        <v-list-item :key="line.payment_id">
                            <v-list-item-avatar tile size="40">
                                <v-img v-if="line.imagekey" :src="$api.getImageUrl(line.imagekey, 80)" contain />
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-semibold text-wrap">{{ lineName(line) }}</v-list-item-title>
                                <v-list-item-subtitle>{{ line.amount | price }}</v-list-item-subtitle>
                                <v-list-item-subtitle v-if="line.canWithdraw" class="text-caption text-wrap">{{ $translate('You can withdraw from this purchase until {0}.').format(formatDayMonth(line.withdrawalDeadline)) }}</v-list-item-subtitle>
                                <v-list-item-subtitle v-else-if="line.withdrawalRequest" class="text-caption text-wrap success--text font-weight-semibold">
                                    <v-icon x-small color="success">mdi-check</v-icon>
                                    {{ $translate('Withdrawal request sent {0}').format(formatDayMonth(line.withdrawalRequest.created)) }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action v-if="line.canWithdraw">
                                <v-btn small color="primary" @click="choose(line)">{{ $translate('Withdraw from purchase') }}</v-btn>
                            </v-list-item-action>
                        </v-list-item>
                    </template>
                </v-list>
                <div class="mt-3 text-caption text--primary">{{ $translate('Questions about the purchase? Contact {0}.').format(facilityName) }}</div>
            </v-card-text>

            <v-card-text v-else-if="step == 'confirm'" class="pt-4">
                <div class="d-flex align-center pa-3 grey lighten-4 rounded">
                    <v-img v-if="chosen.imagekey" :src="$api.getImageUrl(chosen.imagekey, 80)" max-width="40" max-height="40" contain class="mr-3" />
                    <div>
                        <div class="font-weight-semibold text--primary">{{ lineName(chosen) }}</div>
                        <div class="text-caption">{{ chosen.amount | price }} • {{ $translate('purchased {0}').format(formatDate(chosen.purchased)) }}</div>
                    </div>
                </div>
                <template v-if="person.email">
                    <p class="mt-4 text--primary font-weight-medium">{{ $translate('You are withdrawing from {0}, purchased {1}. We will send a confirmation of receipt to {2}. The facility will contact you about the refund.').format(lineName(chosen), formatDate(chosen.purchased), person.email) }}</p>
                    <v-alert v-if="addition" text dense type="info" icon="mdi-information-outline" class="text-body-2 font-weight-medium">{{ $translate(addition) }}</v-alert>
                    <table class="mb-4 person-details">
                        <tr><td class="pr-4 text--secondary font-weight-semibold text-no-wrap">{{ $translate('Name') }}</td><td class="text--primary">{{ person.name }}</td></tr>
                        <tr><td class="pr-4 text--secondary font-weight-semibold text-no-wrap">{{ $translate('E-mail') }}</td><td class="text--primary">{{ person.email }}</td></tr>
                    </table>
                    <v-textarea v-model="message" :label="$translate('Message (optional)')" rows="3" no-resize hide-details maxlength="2000" />
                    <v-alert v-if="error" dense type="error" class="mt-4">{{ error }}</v-alert>
                </template>
                <v-alert v-else dense type="error" class="mt-4">{{ $translate('Add an email address to your account to withdraw from the purchase. We will send the confirmation of receipt there.') }}</v-alert>
            </v-card-text>

            <v-card-text v-else class="text-center pt-8">
                <v-avatar size="64" color="green lighten-5"><v-icon large color="green">mdi-check</v-icon></v-avatar>
                <h3 class="mt-4 text--primary">{{ $translate('Withdrawal request received') }}</h3>
                <p class="mt-2 text--primary font-weight-medium">{{ $translate('We received your withdrawal request on {0} at {1}. A confirmation of receipt has been sent to {2}.').format(formatDate(confirmation.created), formatTime(confirmation.created), confirmation.email) }}</p>
            </v-card-text>

            <v-card-actions v-if="step == 'confirm'" class="px-6" :class="{'flex-column-reverse': $vuetify.breakpoint.xsOnly}">
                <v-btn text color="primary" :block="$vuetify.breakpoint.xsOnly" :disabled="sending" @click="step = 'lines'"><v-icon small>mdi-arrow-left</v-icon> {{ $translate('Back') }}</v-btn>
                <v-spacer />
                <v-btn v-if="person.email" color="primary" :block="$vuetify.breakpoint.xsOnly" :loading="sending" @click="confirm">{{ $translate('Confirm withdrawal') }}</v-btn>
                <v-btn v-else text color="primary" :block="$vuetify.breakpoint.xsOnly" :class="{ 'mr-n2': !$vuetify.breakpoint.xsOnly }" @click="close">{{ $translate('Cancel') }}</v-btn>
            </v-card-actions>
            <v-card-actions v-else class="px-6">
                <v-spacer />
                <v-btn v-if="step == 'lines'" text color="primary" class="mr-n2" @click="close">{{ $translate('Cancel') }}</v-btn>
                <v-btn v-else color="primary" @click="close">{{ $translate('Close') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'zoezi-withdrawal-dialog',

    props: {
        value: { type: Boolean, default: false },
        lines: { type: Array, default: () => [] },
        facilityName: { type: String, default: '' },
        person: { type: Object, default: () => ({}) },
        submit: { type: Function, required: true },
        linkState: { type: String, default: 'ok' },
        expiredDate: { type: [String, Date], default: null },
    },

    data: () => ({
        step: 'lines',
        chosen: null,
        message: '',
        sending: false,
        error: null,
        confirmation: null,
    }),

    computed: {
        linesToShow() {
            return this.lines.filter(line => line.canWithdraw || line.withdrawalRequest);
        },
        hasWithdrawableLine() {
            return this.lines.some(line => line.canWithdraw);
        },
        purchaseHeading() {
            const purchaseDates = new Set(this.linesToShow.map(line => line.purchased));
            if (purchaseDates.size == 1) {
                return this.$translate('Purchase at {0} • {1}').format(this.facilityName, this.formatDate([...purchaseDates][0]));
            }

            return this.$translate('Purchase at {0}').format(this.facilityName);
        },
        addition() {
            return this.chosen ? this.chosen.withdrawalAddition : null;
        },
        stateIcon() {
            if (this.step == 'expired') {
                return { icon: 'mdi-clock-outline', color: 'grey darken-1', background: 'grey lighten-3' };
            }

            return { icon: 'mdi-link-variant-off', color: 'orange darken-2', background: 'orange lighten-5' };
        },
        stateText() {
            if (this.step == 'expired') {
                return this.$translate('The withdrawal period for this purchase ended on {0}. Contact {1} if you have questions.').format(this.formatDate(this.expiredDate), this.facilityName);
            }

            return this.$translate('This link does not work. Contact {0} if you have questions.').format(this.facilityName);
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open) {
                    this.reset();
                }
            },
        },
    },

    methods: {
        reset() {
            this.step = this.linkState == 'ok' ? 'lines' : this.linkState;
            this.chosen = null;
            this.message = '';
            this.error = null;
            this.confirmation = null;
        },
        choose(line) {
            this.chosen = line;
            this.error = null;
            this.step = 'confirm';
        },
        async confirm() {
            // A loading button still takes Enter from the keyboard.
            if (this.sending) {
                return;
            }
            this.sending = true;
            this.error = null;
            try {
                const response = await this.submit(this.chosen.payment_id, this.message);
                // A proxy error page is not JSON, and a network failure has no status.
                const body = await response.json().catch(() => ({}));
                if (response.ok) {
                    this.confirmation = body;
                    this.step = 'done';
                    this.$emit('withdrawn');
                } else {
                    this.error = (response.status && body.message) || this.$translate('Something went wrong');
                }
            } finally {
                this.sending = false;
            }
        },
        close() {
            this.$emit('input', false);
        },
        lineName(line) {
            return line.variant ? `${line.name} - ${line.variant}` : line.name;
        },
        formatDayMonth(value) {
            if (!value) {
                return '';
            }
            const date = Date.newFull(value);
            const month = this.$translate(date.getMonthName());
            const monthText = this.$store.state.language == 'sv' ? month.toLowerCase() : month;

            return `${date.getDate()} ${monthText}`;
        },
        formatDate(value) {
            if (!value) {
                return '';
            }

            return `${this.formatDayMonth(value)} ${Date.newFull(value).getFullYear()}`;
        },
        formatTime(value) {
            return Date.newFull(value).hhmm();
        },
    },
};
</script>

<style scoped>
.font-weight-semibold {
    font-weight: 600 !important;
}

.person-details td {
    vertical-align: top;
}
</style>
